import { roundKg, roundTo } from '../units/round.js';

/**
 * e1RM con Epley ajustado por RIR (docs/LOAD_RECOMMENDATION_ENGINE.md §2).
 * Se usa como INDICADOR DE TENDENCIA, nunca como verdad absoluta, y siempre
 * acompañado de su confianza: proyectar desde repeticiones altas es poco fiable.
 */
export const E1RM_FORMULA_VERSION = 'epley_rir_v1';

export interface E1rmInput {
  loadKg: number;
  reps: number;
  rir: number;
}

export function effectiveReps(input: E1rmInput): number {
  return input.reps + input.rir;
}

export function e1rmEpleyRir(input: E1rmInput): number {
  if (input.loadKg <= 0 || input.reps <= 0) return 0;
  return roundKg(input.loadKg * (1 + effectiveReps(input) / 30));
}

/** Confianza de la estimacion segun las repeticiones equivalentes. */
export function e1rmConfidence(input: E1rmInput): number {
  const reps = effectiveReps(input);
  if (reps <= 5) return 0.95;
  if (reps <= 10) return 0.85;
  if (reps <= 12) return 0.65;
  return 0.4;
}

/** Carga teorica necesaria para un objetivo de repeticiones y RIR. */
export function loadForTarget(e1rmKg: number, targetReps: number, targetRir: number): number {
  const denominator = 1 + (targetReps + targetRir) / 30;
  return roundKg(e1rmKg / denominator);
}

export interface SessionE1rm {
  /** Instante de la sesion, para ordenar. */
  at: string;
  e1rmKg: number;
  confidence: number;
}

export interface CurrentE1rm {
  e1rmKg: number;
  confidence: number;
  sessionsUsed: number;
  /** true si el historial es contradictorio y conviene no dar saltos grandes. */
  noisy: boolean;
}

const EWMA_ALPHA = 0.3;
const MAX_SESSIONS = 5;

/**
 * Estimador robusto: media exponencial del mejor e1RM de cada una de las
 * ultimas sesiones, ponderada por confianza. La progresion NUNCA depende de un
 * unico entrenamiento (requisito §11.1).
 */
export function estimateCurrentE1rm(sessions: readonly SessionE1rm[]): CurrentE1rm | null {
  const usable = [...sessions]
    .filter((session) => session.e1rmKg > 0)
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .slice(-MAX_SESSIONS);

  if (usable.length === 0) return null;

  let value = usable[0]?.e1rmKg ?? 0;
  for (const session of usable.slice(1)) {
    // Una sesion poco fiable mueve menos la estimacion.
    const alpha = EWMA_ALPHA * session.confidence;
    value = value * (1 - alpha) + session.e1rmKg * alpha;
  }

  const mean = usable.reduce((sum, session) => sum + session.e1rmKg, 0) / usable.length;
  const variance =
    usable.reduce((sum, session) => sum + (session.e1rmKg - mean) ** 2, 0) / usable.length;
  const coefficientOfVariation = mean === 0 ? 0 : Math.sqrt(variance) / mean;

  const meanConfidence =
    usable.reduce((sum, session) => sum + session.confidence, 0) / usable.length;

  return {
    e1rmKg: roundKg(value),
    confidence: roundTo(Math.min(meanConfidence, usable.length >= 3 ? 0.95 : 0.6), 2),
    sessionsUsed: usable.length,
    noisy: coefficientOfVariation > 0.08,
  };
}
